import React, { useState, useEffect } from 'react';
import {
  Calendar as BigCalendar,
  momentLocalizer
} from 'react-big-calendar';
import moment from 'moment';
import 'moment/locale/ru';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Chip,
  Typography,
  IconButton,
  Fab,
  Tooltip,
  Alert,
  Snackbar
} from '@mui/material';
import {
  Add,
  Event,
  Person,
  Business,
  Phone,
  Email,
  Close,
  Edit,
  Delete,
  CalendarToday
} from '@mui/icons-material';
import { api } from '../api/client';
import Layout from '../components/Layout';
import { useRealtimeSSE } from '../hooks/useRealtimeSSE';
import 'react-big-calendar/lib/css/react-big-calendar.css';

// Устанавливаем русскую локаль
moment.locale('ru');
const localizer = momentLocalizer(moment);

const CalendarPage = () => {
  // ✅ Подключение к SSE для real-time обновлений
  useRealtimeSSE();

  const [events, setEvents] = useState([]);
  const [open, setOpen] = useState(false);
  const [eventForm, setEventForm] = useState({
    title: '',
    start: '',
    end: ''
  });

  useEffect(() => {
    loadEvents();
  }, []);

  const loadEvents = async () => {
    try {
      const response = await api.get('/events');
      setEvents(response.data.map(event => ({
        ...event,
        start: new Date(event.startTime),
        end: new Date(event.endTime)
      })));
    } catch (error) {
      console.error('Error loading events:', error);
    }
  };

  const handleOpen = () => setOpen(true);
  const handleClose = () => setOpen(false);

  const handleChange = (e) => {
    setEventForm({ ...eventForm, [e.target.name]: e.target.value });
  };

  const handleSubmit = async () => {
    try {
      await api.post('/events', {
        ...eventForm,
        startTime: new Date(eventForm.start),
        endTime: new Date(eventForm.end)
      });
      loadEvents();
      handleClose();
    } catch (error) {
      console.error('Error creating event:', error);
    }
  };

  return (
    <Layout>
      <Box 
        display="flex" 
        justifyContent="space-between" 
        alignItems="center"
        sx={{
          padding: 3,
          marginBottom: 3,
          background: 'rgba(255, 255, 255, 0.1)',
          backdropFilter: 'blur(10px)',
          borderRadius: '15px',
          boxShadow: '0 4px 30px rgba(0, 0, 0, 0.1)',
          border: '1px solid rgba(255, 255, 255, 0.2)'
        }}
      >
        <h1>Календарь</h1>
        <Button variant="contained" color="primary" startIcon={<Add />} onClick={handleOpen}>
          Новое событие
        </Button>
      </Box>
      <Box
        sx={{
          background: 'rgba(255, 255, 255, 0.1)',
          backdropFilter: 'blur(10px)',
          borderRadius: '15px',
          boxShadow: '0 4px 30px rgba(0, 0, 0, 0.1)',
          border: '1px solid rgba(255, 255, 255, 0.2)',
          padding: 2,
          '& .rbc-calendar': {
            background: 'transparent',
          },
          '& .rbc-month-view': {
            background: 'transparent',
          },
          '& .rbc-header': {
            background: 'rgba(255, 255, 255, 0.2)',
            color: 'white',
            fontWeight: 'bold',
            borderBottom: '1px solid rgba(255, 255, 255, 0.3)',
          },
          '& .rbc-date-cell': {
            color: 'white',
          },
          '& .rbc-off-range-bg': {
            background: 'rgba(255, 255, 255, 0.05)',
          },
          '& .rbc-today': {
            background: 'rgba(25, 118, 210, 0.3)',
          },
          '& .rbc-event': {
            background: 'rgba(25, 118, 210, 0.8)',
            border: '1px solid rgba(255, 255, 255, 0.3)',
            backdropFilter: 'blur(5px)',
          },
          '& .rbc-selected': {
            background: 'rgba(255, 255, 255, 0.2)',
          },
          '& .rbc-day-bg:hover': {
            background: 'rgba(255, 255, 255, 0.1)',
          }
        }}
      >
        <BigCalendar
          localizer={localizer}
          events={events}
          startAccessor="start"
          endAccessor="end"
          style={{ height: 600, background: 'transparent' }}
          messages={{
            next: 'Вперёд',
            previous: 'Назад',
            today: 'Сегодня',
            month: 'Месяц',
            week: 'Неделя',
            day: 'День',
            agenda: 'Повестка',
            date: 'Дата',
            time: 'Время',
            event: 'Событие',
            noEventsInRange: 'Нет событий в этом диапазоне',
            showMore: total => `+ еще ${total}`
          }}
        />
      </Box>
      <Dialog 
        open={open} 
        onClose={handleClose}
        PaperProps={{
          sx: {
            background: 'rgba(255, 255, 255, 0.1)',
            backdropFilter: 'blur(20px)',
            borderRadius: '15px',
            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)',
            border: '1px solid rgba(255, 255, 255, 0.2)',
            color: 'white'
          }
        }}
      >
        <DialogTitle>Создать событие</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Название"
            type="text"
            fullWidth
            name="title"
            value={eventForm.title}
            onChange={handleChange}
            sx={{
              '& .MuiInputLabel-root': { color: 'rgba(255, 255, 255, 0.7)' },
              '& .MuiOutlinedInput-root': {
                '& fieldset': { borderColor: 'rgba(255, 255, 255, 0.3)' },
                '&:hover fieldset': { borderColor: 'rgba(255, 255, 255, 0.5)' },
                '&.Mui-focused fieldset': { borderColor: 'rgba(25, 118, 210, 0.8)' },
                color: 'white',
                background: 'rgba(255, 255, 255, 0.1)',
                backdropFilter: 'blur(5px)'
              }
            }}
          />
          <TextField
            margin="dense"
            label="Начало"
            type="datetime-local"
            fullWidth
            name="start"
            value={eventForm.start}
            onChange={handleChange}
            sx={{
              '& .MuiInputLabel-root': { color: 'rgba(255, 255, 255, 0.7)' },
              '& .MuiOutlinedInput-root': {
                '& fieldset': { borderColor: 'rgba(255, 255, 255, 0.3)' },
                '&:hover fieldset': { borderColor: 'rgba(255, 255, 255, 0.5)' },
                '&.Mui-focused fieldset': { borderColor: 'rgba(25, 118, 210, 0.8)' },
                color: 'white',
                background: 'rgba(255, 255, 255, 0.1)',
                backdropFilter: 'blur(5px)'
              }
            }}
          />
          <TextField
            margin="dense"
            label="Окончание"
            type="datetime-local"
            fullWidth
            name="end"
            value={eventForm.end}
            onChange={handleChange}
            sx={{
              '& .MuiInputLabel-root': { color: 'rgba(255, 255, 255, 0.7)' },
              '& .MuiOutlinedInput-root': {
                '& fieldset': { borderColor: 'rgba(255, 255, 255, 0.3)' },
                '&:hover fieldset': { borderColor: 'rgba(255, 255, 255, 0.5)' },
                '&.Mui-focused fieldset': { borderColor: 'rgba(25, 118, 210, 0.8)' },
                color: 'white',
                background: 'rgba(255, 255, 255, 0.1)',
                backdropFilter: 'blur(5px)'
              }
            }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose} color="primary">
            Отмена
          </Button>
          <Button onClick={handleSubmit} color="primary">
            Сохранить
          </Button>
        </DialogActions>
      </Dialog>
    </Layout>
  );
};

export default CalendarPage;
